import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetProductCategoriesV1ProductCategoriesGetData,
  type GetProductCategoriesV1ProductCategoriesGetError,
  type GetProductCategoriesV1ProductCategoriesGetResponse,
  getProductCategoriesV1ProductCategoriesGet,
} from '../../generated';

export type GetProductCategoriesQueryVariables = NonNullable<
  GetProductCategoriesV1ProductCategoriesGetData['query']
> & {
  organization_id?: string;
  portfolio_id?: string;
};

export type GetProductCategoriesQuery = {
  data: GetProductCategoriesV1ProductCategoriesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useGetProductCategoriesQuery = <
  TData = GetProductCategoriesQuery,
  TError = GetProductCategoriesV1ProductCategoriesGetError,
>(
  {
    organization_id,
    portfolio_id,
    breadcrumbs_like,
    limit,
    name_like,
    offset,
    sort_by,
    sort_direction,
    l1_category_id,
    category_ids,
    scope_to_ICP,
    scope_to_portfolio,
  }: GetProductCategoriesQueryVariables,
  options?: UseQueryOptions<GetProductCategoriesQuery, TError, TData>,
) => {
  return useQuery<GetProductCategoriesQuery, TError, TData>({
    queryKey: [
      'GetProductCategories',
      {
        organization_id,
        portfolio_id,
        breadcrumbs_like,
        limit,
        name_like,
        offset,
        sort_by,
        sort_direction,
        l1_category_id,
        category_ids,
        scope_to_ICP,
        scope_to_portfolio,
      },
    ],
    queryFn: async () => {
      const { data, response } = await getProductCategoriesV1ProductCategoriesGet({
        query: {
          breadcrumbs_like,
          limit,
          name_like,
          offset,
          sort_by,
          sort_direction,
          l1_category_id,
          category_ids,
          scope_to_ICP,
          scope_to_portfolio,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useGetProductCategoriesQuery.getKey = (variables: GetProductCategoriesQueryVariables & { organizationId: string }) => [
  'GetProductCategories',
  variables,
];

useGetProductCategoriesQuery.fetcher = async (
  {
    breadcrumbs_like,
    limit,
    name_like,
    offset,
    sort_by,
    sort_direction,
    l1_category_id,
    category_ids,
    scope_to_ICP,
    scope_to_portfolio,
  }: GetProductCategoriesQueryVariables,
  _headers?: RequestInit['headers'],
): Promise<GetProductCategoriesQuery> => {
  const { data, response } = await getProductCategoriesV1ProductCategoriesGet({
    query: {
      breadcrumbs_like,
      limit,
      name_like,
      offset,
      sort_by,
      sort_direction,
      l1_category_id,
      category_ids,
      scope_to_ICP,
      scope_to_portfolio,
    },
    throwOnError: true,
  });
  return {
    data: data ?? [],
    total_count: extractTotalCount(response),
  };
};
